import { translateFn } from '../../../../../../../../../utils/translator.helper';
import { FormValidator, ValidationResult } from '../../../../../../../../../ui/gzForm/formField/types/form-validator';
import {
  AntimalwareSettingsExclusionsItem,
  AntimalwareSettingsExclusionsTaskType,
} from '@nirvana/shared/dtos/types/policies/common';
import { IpValidator } from '../../../../../../../../../ui/gzForm/validators/ip.validator';
import { wildcard } from '../../../../../../../../common/policyPanel/uiSettings/utils/validators/wildcard.validators';
import { CustomizableValidator } from '../../../../../../../../../ui/gzForm/validators/customizable.validator';
import {
  exclusionAbsoluteFilePathWithWildcards,
  exclusionAbsolutePathWithWildcards,
} from '../../../../../../../../common/policyPanel/uiSettings/utils/validators/path.validators';
import { extension } from '../../../../../../../../common/policyPanel/uiSettings/utils/validators/extension.validators';
import { sha256Multiple } from '../../../../../../../../common/policyPanel/uiSettings/utils/validators/sha256.validators';
import { thumbprint } from '../../../../../../../../common/policyPanel/uiSettings/utils/validators/thumbprint.validators';
import { RequiredValidator } from '../../../../../../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../../../../../../ui/gzForm/validators/max-length.validator';

// maximum entity length
export const MAX_LENGTH_NON_PATH = 260;
export const MAX_LENGTH_PATH = 4096;
export const MAX_LENGTH_CMD = 2047;
export const MAX_LENGTH_REMARKS = 500;

// validate entity by type
export function getPathValidatorsByType(type: AntimalwareSettingsExclusionsTaskType): FormValidator<string>[] {
  let customValidator;
  let maxLength = MAX_LENGTH_NON_PATH;

  switch (type) {
    case AntimalwareSettingsExclusionsTaskType.FILE:
      maxLength = MAX_LENGTH_PATH;
      customValidator = CustomizableValidator(exclusionAbsoluteFilePathWithWildcards);
      break;
    case AntimalwareSettingsExclusionsTaskType.FOLDER:
      maxLength = MAX_LENGTH_PATH;
      customValidator = CustomizableValidator(exclusionAbsolutePathWithWildcards);
      break;
    case AntimalwareSettingsExclusionsTaskType.EXTENSION:
      customValidator = extension;
      break;
    case AntimalwareSettingsExclusionsTaskType.PROCESS:
      maxLength = MAX_LENGTH_PATH;
      customValidator = CustomizableValidator(exclusionAbsoluteFilePathWithWildcards);
      break;
    case AntimalwareSettingsExclusionsTaskType.FILE_HASH:
      customValidator = CustomizableValidator(sha256Multiple);
      break;
    case AntimalwareSettingsExclusionsTaskType.CERTIFICATE_HASH:
      customValidator = CustomizableValidator(thumbprint);
      break;
    case AntimalwareSettingsExclusionsTaskType.THREAT_NAME:
      customValidator = CustomizableValidator(value => wildcard(value, translateFn('Invalid detection name')));
      break;
    case AntimalwareSettingsExclusionsTaskType.CMD_PARAMS:
      maxLength = MAX_LENGTH_CMD;
      break;
    case AntimalwareSettingsExclusionsTaskType.IP_MASK:
      customValidator = IpValidator(translateFn('Invalid IP address'));
      break;
  }

  // compute validators for field
  const validators = [
    RequiredValidator(translateFn('Required field')),
    MaxLengthValidator(
      maxLength,
      translateFn('The excluded item must have a maximum of {maxLength} characters.', { maxLength: maxLength })
    ),
  ];
  if (customValidator) {
    validators.push(customValidator);
  }

  return validators;
}

// validate duplicates
export function validateDuplicates(
  exclusionItem: AntimalwareSettingsExclusionsItem,
  exclusionsInGrid: AntimalwareSettingsExclusionsItem[]
): ValidationResult {
  const isUnixPath = ['/', '~'].indexOf(exclusionItem.path.charAt(0)) !== -1;
  const hasDuplicates = exclusionsInGrid.some((exclusionInGrid: AntimalwareSettingsExclusionsItem) => {
    const sameType = exclusionInGrid.taskType === exclusionItem.taskType;
    const sameValue =
      (isUnixPath && exclusionInGrid.path === exclusionItem.path) ||
      (!isUnixPath && exclusionInGrid.path.toLowerCase() === exclusionItem.path.toLowerCase());
    const sameModule = exclusionItem.module.some(module => exclusionInGrid.module.includes(module));

    return sameType && sameValue && sameModule;
  });

  if (hasDuplicates) {
    return {
      valid: false,
      error: translateFn('The entry already exists.'),
    };
  }

  return {
    valid: true,
  };
}
